/* Mobile scoring view rendered when the URL is /cup/<token>. The token
   is the only auth — it identifies the workspace, the session, and the
   specific cupper. The page is bare-chrome on purpose: this is what
   someone clicks open on their phone next to a cup, so it should load
   in two seconds, work one-handed, and not look like a B2B dashboard. */
import React, { useEffect, useMemo, useState } from "react";

const PRIMARY = [
  { key: "fragrance",  label: "Fragrance / Aroma" },
  { key: "flavor",     label: "Flavor" },
  { key: "aftertaste", label: "Aftertaste" },
  { key: "acidity",    label: "Acidity" },
  { key: "body",       label: "Body" },
  { key: "balance",    label: "Balance" },
  { key: "overall",    label: "Overall" },
];
const SECONDARY = [
  { key: "uniformity", label: "Uniformity" },
  { key: "cleanCup",   label: "Clean cup" },
  { key: "sweetness",  label: "Sweetness" },
];

function djb2(src) {
  let h = 5381;
  for (let i = 0; i < src.length; i++) h = ((h << 5) + h) + src.charCodeAt(i);
  return Math.abs(h).toString(36).toUpperCase().slice(0, 3).padStart(3, "X");
}
function totalFrom(scores, defects) {
  if (!scores) return 0;
  if (!PRIMARY.some(p => scores[p.key] != null)) return 0;
  const prim = PRIMARY.reduce((a, p) => a + (Number(scores[p.key]) || 0), 0);
  const sec = SECONDARY.reduce((a, s) => a + (scores[s.key] != null ? Number(scores[s.key]) : 10), 0);
  return +(prim + sec - (Number(defects) || 0)).toFixed(2);
}

function Slider({ label, value, onChange }) {
  // Value is null until the cupper drags. We treat 6.0 as the floor
  // (anything below that is "not specialty" and irrelevant for this
  // form) and 10.0 as the ceiling.
  const display = value == null ? "—" : value.toFixed(2);
  const v = value == null ? 8.0 : value;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{label}</span>
        <span style={{
          fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums",
          color: value == null ? "#D1D5DB" : "#111",
        }}>{display}</span>
      </div>
      <input
        type="range" min={6} max={10} step={0.25}
        value={v}
        onInput={(e) => onChange(parseFloat(e.target.value))}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: "100%", accentColor: "#000" }}
      />
    </div>
  );
}

function SampleCard({ sample, sampleId, token, onSaved }) {
  const initial = sample.myRecord?.scores || {};
  const [scores, setScores] = useState(initial);
  const [defects, setDefects] = useState(sample.myRecord?.defects || 0);
  const [notes, setNotes] = useState(sample.myRecord?.notes || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(sample.myRecord?.scoredAt || null);
  const [err, setErr] = useState("");

  const update = (k, v) => setScores(prev => ({ ...prev, [k]: v }));
  const liveTotal = useMemo(() => totalFrom(scores, defects), [scores, defects]);
  const dirty = useMemo(() => {
    const a = sample.myRecord?.scores || {};
    const keys = [...PRIMARY, ...SECONDARY].map(x => x.key);
    if (keys.some(k => (a[k] ?? null) !== (scores[k] ?? null))) return true;
    if ((sample.myRecord?.defects || 0) !== (Number(defects) || 0)) return true;
    if ((sample.myRecord?.notes || "") !== notes) return true;
    return false;
  }, [scores, defects, notes, sample.myRecord]);

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const r = await fetch(`/api/cup/${token}/score`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coffeeId: sample.id,
          scores,
          defects: Number(defects) || 0,
          notes,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't save");
      }
      setSavedAt(Date.now());
      onSaved?.(sample.id);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const tColor = liveTotal >= 90 ? "#B8860B" : liveTotal >= 85 ? "#16A34A" : liveTotal >= 80 ? "#111" : "#6B7280";
  const tGrade = liveTotal >= 90 ? "Outstanding" : liveTotal >= 85 ? "Excellent" : liveTotal >= 80 ? "Very Good" : liveTotal > 0 ? "Below Specialty" : "—";

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: 18, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{
          background: "#000", color: "#fff", borderRadius: 999,
          padding: "6px 14px", fontSize: 14, fontWeight: 800,
          letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
        }}>{sampleId}</span>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: tColor, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {liveTotal > 0 ? liveTotal.toFixed(2) : "—"}
          </div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginTop: 2 }}>{tGrade}</div>
        </div>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 2, lineHeight: 1.25 }}>
        {sample.coffeeName || "Unnamed sample"}
      </div>
      {(sample.sourcedFrom || sample.expectedNotes) && (
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 14, lineHeight: 1.45 }}>
          {sample.sourcedFrom}{sample.sourcedFrom && sample.expectedNotes ? " · " : ""}{sample.expectedNotes}
        </div>
      )}

      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
        {PRIMARY.map(p => (
          <Slider key={p.key} label={p.label} value={scores[p.key] ?? null} onChange={(v) => update(p.key, v)} />
        ))}
      </div>
      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12, marginTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
          Secondary checks (default 10 — drag down for deductions)
        </div>
        {SECONDARY.map(s => (
          <Slider key={s.key} label={s.label} value={scores[s.key] ?? 10} onChange={(v) => update(s.key, v)} />
        ))}
      </div>

      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12, marginTop: 4 }}>
        <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 6 }}>Defect points</label>
        <input
          type="number" min={0} step={1}
          value={defects}
          onChange={(e) => setDefects(e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
          inputMode="numeric"
          style={{ width: "100%", padding: "12px 14px", border: "1px solid #E5E7EB", borderRadius: 10, fontSize: 16, fontFamily: "Inter, sans-serif", background: "#fff" }}
        />
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 6 }}>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Descriptors, impressions, defects observed…"
          style={{ width: "100%", padding: "12px 14px", border: "1px solid #E5E7EB", borderRadius: 10, fontSize: 15, fontFamily: "Inter, sans-serif", background: "#fff", resize: "vertical" }}
        />
      </div>

      {err && (
        <div style={{ marginTop: 12, padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{err}</div>
      )}

      <button
        onClick={submit}
        disabled={saving || !dirty}
        style={{
          marginTop: 14, width: "100%",
          padding: "14px 18px", borderRadius: 12,
          background: dirty ? "#000" : "#E5E7EB",
          color: dirty ? "#fff" : "#6B7280",
          fontSize: 16, fontWeight: 700, border: "none",
          cursor: dirty && !saving ? "pointer" : "default",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {saving ? "Saving…" : savedAt && !dirty ? "Saved ✓" : "Save score"}
      </button>
    </div>
  );
}

export default function CupperMobile({ token }) {
  const [state, setState] = useState({ status: "loading", data: null, err: "" });
  const [savedIds, setSavedIds] = useState(new Set());
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/cup/${token}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.error || "Couldn't load");
        }
        const data = await r.json();
        if (!cancelled) setState({ status: "ready", data, err: "" });
      } catch (e) {
        if (!cancelled) setState({ status: "error", data: null, err: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const sessionPrefix = state.data ? djb2(state.data.session.id || state.data.session.name || "session") : "";

  const markComplete = async () => {
    setMarking(true);
    try {
      await fetch(`/api/cup/${token}/complete`, { method: "POST" });
      setState(s => ({ ...s, data: { ...s.data, completedAt: Date.now() } }));
    } finally { setMarking(false); }
  };

  if (state.status === "loading") {
    return (
      <Shell>
        <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontSize: 15 }}>Loading…</div>
      </Shell>
    );
  }
  if (state.status === "error") {
    return (
      <Shell>
        <div style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>This link won't open</h1>
          <p style={{ fontSize: 14, color: "#6B7280", margin: 0, lineHeight: 1.55 }}>{state.err}</p>
        </div>
      </Shell>
    );
  }

  const { cupper, session, samples, completedAt } = state.data;
  const totalSamples = samples.length;
  const scoredCount = samples.filter(s =>
    savedIds.has(s.id) || (s.myRecord?.scores && PRIMARY.some(p => s.myRecord.scores[p.key] != null))
  ).length;
  // Guest cuppers were invited by email without a roster entry, so the
  // server has no name for them. Block the scoring UI behind a simple
  // "what's your first name?" prompt — once they submit it, the name
  // propagates back to every sample's record so the dashboard's tab
  // labels read correctly.
  if (!cupper?.name) {
    return (
      <Shell>
        <IdentifyForm
          token={token}
          sessionName={session.name}
          onIdentified={(name) => setState(s => ({ ...s, data: { ...s.data, cupper: { ...s.data.cupper, name } } }))}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{ padding: "20px 16px 24px", borderBottom: "1px solid #F3F4F6", background: "#fff" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Cupping · {cupper.name}
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111", margin: "4px 0 6px", lineHeight: 1.2 }}>{session.name}</h1>
        <div style={{ fontSize: 13, color: "#6B7280" }}>
          {scoredCount} of {totalSamples} scored
          {session.cuppingDate ? <> · {new Date(session.cuppingDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</> : null}
        </div>
        {session.notes && (
          <div style={{ marginTop: 10, padding: "10px 12px", background: "#FAFAFA", borderLeft: "3px solid #2C5766", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{session.notes}</div>
        )}
      </div>
      <div style={{ padding: 16 }}>
        {samples.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#6B7280", fontSize: 14 }}>No samples in this session yet.</div>
        ) : (
          samples.map((s, i) => (
            <SampleCard
              key={s.id}
              sample={s}
              sampleId={`${sessionPrefix}-${String(i + 1).padStart(2, "0")}`}
              token={token}
              onSaved={(id) => setSavedIds(prev => new Set(prev).add(id))}
            />
          ))
        )}
        {samples.length > 0 && (
          <button
            onClick={markComplete}
            disabled={marking || !!completedAt}
            style={{
              width: "100%", marginTop: 8,
              padding: "14px 18px", borderRadius: 12,
              background: completedAt ? "#F3F4F6" : "#fff",
              color: completedAt ? "#6B7280" : "#111",
              border: `1px solid ${completedAt ? "#E5E7EB" : "#111"}`,
              fontSize: 15, fontWeight: 700,
              cursor: completedAt ? "default" : "pointer",
              fontFamily: "Inter, sans-serif",
            }}
          >{completedAt ? "Marked done ✓" : marking ? "Marking…" : "Mark cupping done"}</button>
        )}
      </div>
    </Shell>
  );
}

function IdentifyForm({ token, sessionName, onIdentified }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await fetch(`/api/cup/${token}/identify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't save");
      }
      onIdentified(name.trim());
    } catch (e) {
      setErr(e.message); setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} style={{ padding: "32px 20px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        Cupping invite
      </div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111", margin: "0 0 18px", lineHeight: 1.2 }}>{sessionName}</h1>
      <p style={{ fontSize: 15, color: "#374151", margin: "0 0 18px", lineHeight: 1.5 }}>
        What's your first name? We'll show this on the cupping sheet so the team knows whose scores are whose.
      </p>
      <input
        type="text"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="First name"
        autoComplete="given-name"
        style={{ width: "100%", padding: "14px 16px", border: "1px solid #E5E7EB", borderRadius: 12, fontSize: 17, fontFamily: "Inter, sans-serif", marginBottom: 12 }}
      />
      {err && <div style={{ marginBottom: 12, padding: "10px 12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, color: "#991B1B", fontSize: 13 }}>{err}</div>}
      <button
        type="submit"
        disabled={busy || !name.trim()}
        style={{
          width: "100%", padding: "14px 18px", borderRadius: 12,
          background: name.trim() ? "#000" : "#E5E7EB",
          color: name.trim() ? "#fff" : "#6B7280",
          fontSize: 16, fontWeight: 700, border: "none",
          cursor: busy ? "default" : (name.trim() ? "pointer" : "default"),
          fontFamily: "Inter, sans-serif",
        }}
      >{busy ? "Saving…" : "Continue"}</button>
    </form>
  );
}

function Shell({ children }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#F9FAFB",
      fontFamily: "Inter, -apple-system, sans-serif", color: "#111",
      maxWidth: 560, margin: "0 auto",
    }}>
      {children}
    </div>
  );
}
